export type RouteContextFamily = "client" | "notary" | "neutral";

export type OverlayPolicyKey = "notary_missing_contact_email" | "splash_notice";

type ContextFamilyStrategyMetadata = {
	allowedOverlays: OverlayPolicyKey[];
	blockedOverlays: OverlayPolicyKey[];
	authFlowBlockedOverlays: OverlayPolicyKey[];
};

export const CLIENT_CONTEXT_ROUTE_PREFIXES: string[] = [
	"/client-dashboard",
	"/customers/login",
	"/customer-login",
	"/third-party",
	/** Customer / third-party folder picker — must stay client context (not `/folders` notary dashboard). */
	"/folders/select",
];

export const NOTARY_CONTEXT_ROUTE_PREFIXES: string[] = [
	"/folder",
	"/folders",
	"/offices",
	"/users",
	"/collaborators",
	"/subscription",
	"/document-types",
	"/deed-types",
	"/admin",
	"/super-admin",
	"/my-account",
	"/office-directory",
	"/notes",
	"/legal",
];

export const AUTH_FLOW_ROUTE_PREFIXES: string[] = [
	"/login",
	"/authorized-client",
	"/customer-login",
	"/customers/login",
	"/third-party/login",
];

const CONTEXT_STRATEGY_METADATA: Record<RouteContextFamily, ContextFamilyStrategyMetadata> = {
	client: {
		allowedOverlays: [],
		blockedOverlays: ["notary_missing_contact_email", "splash_notice"],
		authFlowBlockedOverlays: ["notary_missing_contact_email", "splash_notice"],
	},
	notary: {
		allowedOverlays: ["notary_missing_contact_email", "splash_notice"],
		blockedOverlays: [],
		authFlowBlockedOverlays: ["notary_missing_contact_email", "splash_notice"],
	},
	neutral: {
		allowedOverlays: [],
		blockedOverlays: ["notary_missing_contact_email", "splash_notice"],
		authFlowBlockedOverlays: ["notary_missing_contact_email", "splash_notice"],
	},
};

type EffectiveRouteContextOptions = {
	isCustomer?: boolean;
	isThirdParty?: boolean;
};

function normalizePathname(pathname: string | null | undefined): string {
	return pathname ?? "";
}

export function isClientContextRoute(pathname: string | null | undefined): boolean {
	const path = normalizePathname(pathname);
	if (!path) {
		return false;
	}
	return CLIENT_CONTEXT_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function isAuthFlowRoute(pathname: string | null | undefined): boolean {
	const path = normalizePathname(pathname);
	if (!path) {
		return false;
	}
	return AUTH_FLOW_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function resolveRouteContextFamily(pathname: string | null | undefined): RouteContextFamily {
	const path = normalizePathname(pathname);
	if (!path) {
		return "neutral";
	}
	if (isClientContextRoute(path)) {
		return "client";
	}
	if (NOTARY_CONTEXT_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix))) {
		return "notary";
	}
	return "neutral";
}

export function resolveEffectiveRouteContextFamily(
	pathname: string | null | undefined,
	options: EffectiveRouteContextOptions = {},
): RouteContextFamily {
	if (options.isCustomer || options.isThirdParty) {
		return "client";
	}
	return resolveRouteContextFamily(pathname);
}

export function getContextFamilyStrategyMetadata(family: RouteContextFamily): ContextFamilyStrategyMetadata {
	return CONTEXT_STRATEGY_METADATA[family];
}

export function isOverlayBlockedForPath(params: {
	overlay: OverlayPolicyKey;
	pathname: string | null | undefined;
	routeContextFamily: RouteContextFamily;
}): boolean {
	const { overlay, pathname, routeContextFamily } = params;
	const strategy = getContextFamilyStrategyMetadata(routeContextFamily);
	if (strategy.blockedOverlays.includes(overlay)) {
		return true;
	}
	if (isAuthFlowRoute(pathname) && strategy.authFlowBlockedOverlays.includes(overlay)) {
		return true;
	}
	return false;
}
